import React__default from 'react';
import styled from 'styled-components';
import Text from '../components/Text/Text.js';
import AtlasButton from '../components/AtlasButton/AtlasButton.js';
import Box from '../components/Box/Box.js';
import Spacer from '../components/Spacer/Spacer.js';
import { get } from '../constants.js';
import { Card } from '../components/Card/Card.js';

/**
 * Every Getting Started page Button Card provides links that give users actions to take.
 *
 * Those actions can be internal, external, or addon actions.
 */

/**
 * The type of analytics metrics your application collects.
 */

/**
 * The ButtonCard component is a border-less `Card` component that provides detail about key functionality, and link to documentation
 * that helps people understand how to use your product.
 * 
 * You'll want to ensure that you provide an array of list items, each of which should adhere to the following shape:
 * 
 * ```typescript
 * 
 * type ButtonCardItem = {
    itemPrimaryAction: ButtonCardAction
    itemDocsAction: ButtonCardAction
    label?: string
    description?: string
}
 * ```
 *
 * The `ButtonCardAction` shares an identical shape to the other Getting Started Page component action types:
 * 
 * ```typescript
 * type ButtonCardAction = {
 *  type: 'EXTERNAL' | 'INTERNAL' | 'ADDON'
      url: string
      label: string
      tracking: {
        destination: string
        type: 'button' | 'link' | 'video'
      }
 * }
 *```
 */
const ButtonCard = ({
  title,
  caption,
  primaryAction,
  secondaryAction,
  actionHandler
}) => {
  console.warn('ButtonCard is deprecated. Use the v1.1 layout for Getting Started screens instead. See: https://www.fuegokit.design/react-storybook/?path=/story/patterns-getting-started-page--getting-started-page-growth');
  return /*#__PURE__*/React__default.createElement(Wrapper, {
    as: "article"
  }, /*#__PURE__*/React__default.createElement(Card.Content, {
    sx: {
      justifyContent: 'revert'
    }
  }, /*#__PURE__*/React__default.createElement(Card.Header, null, /*#__PURE__*/React__default.createElement(Text, {
    as: "h3",
    sx: {
      fontWeight: 700,
      fontSize: 5,
      lineHeight: 24 / 20,
      margin: 'auto',
      color: 'text.default'
    }
  }, title)), /*#__PURE__*/React__default.createElement(Card.Body, {
    sx: {
      minHeight: '140px',
      fontSize: 3,
      lineHeight: 'normal'
    }
  }, /*#__PURE__*/React__default.createElement(Text, {
    as: "p",
    sx: {
      lineHeight: 20 / 14,
      marginTop: '10px',
      color: 'text.subtle'
    }
  }, caption))), /*#__PURE__*/React__default.createElement(Card.Actions, {
    sx: {
      display: 'flex',
      alignItems: 'flex-start',
      fontSize: '3'
    }
  }, /*#__PURE__*/React__default.createElement(AtlasButton, {
    appearance: `primary`,
    as: "button",
    sx: {
      fontWeight: 'bold'
    }
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    ,
    onClick: () => actionHandler ? actionHandler(primaryAction) : null,
    "data-testid": "btn-card-btn-primary"
  }, primaryAction === null || primaryAction === void 0 ? void 0 : primaryAction.label), /*#__PURE__*/React__default.createElement(AtlasButton, {
    appearance: `subtle-link`,
    as: "a",
    href: secondaryAction === null || secondaryAction === void 0 ? void 0 : secondaryAction.url,
    sx: {
      color: 'text.subtlest'
    }
  }, /*#__PURE__*/React__default.createElement(Box, {
    as: "span",
    sx: {
      display: 'flex',
      alignItems: 'baseline',
      justifyContent: 'center'
    }
  }, /*#__PURE__*/React__default.createElement(SVG, {
    width: "16",
    height: "16",
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React__default.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M6 17H5V7H6V5H5C4.46957 5 3.96086 5.21071 3.58579 5.58579C3.21071 5.96086 3 6.46957 3 7V17C3 17.5304 3.21071 18.0391 3.58579 18.4142C3.96086 18.7893 4.46957 19 5 19H6V17ZM19 10.005V19H9V5H14.99V7.49C14.99 7.68698 15.0288 7.88204 15.1042 8.06402C15.1796 8.24601 15.2901 8.41137 15.4293 8.55066C15.5686 8.68995 15.734 8.80044 15.916 8.87582C16.098 8.9512 16.293 8.99 16.49 8.99H20.99V8.468C20.99 8.171 20.858 7.89 20.631 7.7L15.557 3.464C15.197 3.164 14.744 3 14.275 3H9C8.46957 3 7.96086 3.21071 7.58579 3.58579C7.21071 3.96086 7 4.46957 7 5V19C7 19.5304 7.21071 20.0391 7.58579 20.4142C7.96086 20.7893 8.46957 21 9 21H19C19.5304 21 20.0391 20.7893 20.4142 20.4142C20.7893 20.0391 21 19.5304 21 19V10.005H19Z",
    fill: "currentColor"
  })), ' ', /*#__PURE__*/React__default.createElement(Spacer, {
    size: 12,
    axis: `horizontal`
  }), "Documentation"))));
};
ButtonCard.displayName = "ButtonCard";
const Wrapper = styled(Card).withConfig({
  displayName: "ButtonCard__Wrapper",
  componentId: "sc-h9nqa9-0"
})(["display:flex;flex-direction:column;@media (min-width:", "){max-width:385px;}@media (min-width:", "){max-width:450px;}"], get('breakpoints.1'), get('breakpoints.2'));
const SVG = styled.svg.withConfig({
  displayName: "ButtonCard__SVG",
  componentId: "sc-h9nqa9-1"
})(["display:block;user-select:none;overflow:visible;"]);
var ButtonCard$1 = ButtonCard;

export { ButtonCard$1 as default };
